import type {
  SpaceLabelFitInput,
  SpaceLabelFitResult
} from 'utils/space-label-fit-types';
import {
  estimateSpaceLabelSize,
  labelHasUnwrappableOverflow,
  measureUnwrappedLabelWidth
} from 'utils/space-label-size';
import { abbreviateSpaceName } from 'utils/space-name-abbreviation';

export type {
  SpaceLabelFitInput,
  SpaceLabelFitResult
} from 'utils/space-label-fit-types';

const LABEL_INSET = 0.9;

function spaceLabelFitsScreen(
  label: string,
  screenMaxWidth: number,
  screenMaxHeight: number,
  options?: {
    showDimensions?: boolean;
    dimensionsText?: string;
    showBtus?: boolean;
    showToggle?: boolean;
    allowWrap?: boolean;
  }
): boolean {
  if (screenMaxWidth <= 0 || screenMaxHeight <= 0) {
    return false;
  }
  if (labelHasUnwrappableOverflow(label, screenMaxWidth)) {
    return false;
  }
  if (options?.allowWrap === false) {
    const unwrappedWidth = measureUnwrappedLabelWidth(label);
    if (unwrappedWidth > screenMaxWidth) {
      return false;
    }
  }
  const size = estimateSpaceLabelSize(label, {
    ...options,
    maxWidth:
      options?.allowWrap === false ? Number.POSITIVE_INFINITY : screenMaxWidth
  });
  return size.width <= screenMaxWidth && size.height <= screenMaxHeight;
}

export function resolveSpaceLabelFit(
  input: SpaceLabelFitInput
): SpaceLabelFitResult {
  const zoom = input.zoom > 0 ? input.zoom : 1;
  const screenMaxWidth = Math.max(1, input.spaceWidth * zoom * LABEL_INSET);
  const screenMaxHeight = Math.max(1, input.spaceHeight * zoom * LABEL_INSET);
  const abbreviated = abbreviateSpaceName(input.label);
  const nameFits = spaceLabelFitsScreen(
    input.label,
    screenMaxWidth,
    screenMaxHeight,
    { showToggle: input.showToggle, allowWrap: input.allowWrap }
  );
  const displayLabel = nameFits ? input.label : abbreviated;
  const isAbbreviated = displayLabel !== input.label;

  const showDimensionsOnSpace =
    !!input.showDimensions &&
    spaceLabelFitsScreen(displayLabel, screenMaxWidth, screenMaxHeight, {
      showDimensions: true,
      dimensionsText: input.dimensionsText,
      showToggle: input.showToggle,
      allowWrap: input.allowWrap
    });

  const showBtusOnSpace =
    !!input.showBtus &&
    spaceLabelFitsScreen(displayLabel, screenMaxWidth, screenMaxHeight, {
      showDimensions: showDimensionsOnSpace,
      dimensionsText: input.dimensionsText,
      showBtus: true,
      showToggle: input.showToggle,
      allowWrap: input.allowWrap
    });

  const screenSize = estimateSpaceLabelSize(displayLabel, {
    showDimensions: showDimensionsOnSpace,
    dimensionsText: input.dimensionsText,
    showBtus: showBtusOnSpace,
    showToggle: input.showToggle,
    maxWidth:
      input.allowWrap === false ? Number.POSITIVE_INFINITY : screenMaxWidth
  });

  return {
    displayLabel,
    isAbbreviated,
    showBtusOnSpace,
    showBtusInHover: !!input.showBtus && !showBtusOnSpace,
    showDimensionsOnSpace,
    showDimensionsInHover: !!input.showDimensions && !showDimensionsOnSpace,
    screenMaxWidth,
    screenSize,
    worldSize: {
      width: screenSize.width / zoom,
      height: screenSize.height / zoom
    }
  };
}
